/**
 * Transcript edit operations.
 *
 * Core rule: editing *text* must never destroy *timing*. Every operation here
 * is pure (returns new arrays) so it composes cleanly with an undo/redo stack
 * built from inverse operations rather than full-project snapshots.
 */
import { indexWords, recomputeSegmentTiming } from './segmentation.js';
import { localId } from './ids.js';
export function correctWordText(words, wordId, newText) {
    return words.map((w) => (w.id === wordId ? { ...w, text: newText } : w));
}
/** Hide a word from captions while preserving its timing slot. */
export function setWordHidden(words, wordId, hidden) {
    return words.map((w) => (w.id === wordId ? { ...w, hidden } : w));
}
export function setWordEmphasis(words, wordId, emphasized) {
    return words.map((w) => (w.id === wordId ? { ...w, emphasized } : w));
}
/**
 * Split one word into two, dividing its time window proportionally by text
 * length. Timing of surrounding words is untouched.
 */
export function splitWord(words, wordId, atChar) {
    const idx = words.findIndex((w) => w.id === wordId);
    if (idx === -1)
        return words;
    const w = words[idx];
    const chars = [...w.text];
    if (atChar <= 0 || atChar >= chars.length)
        return words;
    const leftText = chars.slice(0, atChar).join('');
    const rightText = chars.slice(atChar).join('');
    const frac = leftText.length / chars.length;
    const mid = Math.round(w.startMs + (w.endMs - w.startMs) * frac);
    const left = { ...w, text: leftText, endMs: mid };
    const right = { ...w, id: localId('w'), text: rightText, startMs: mid };
    return [...words.slice(0, idx), left, right, ...words.slice(idx + 1)];
}
/** Merge a word with the next one, spanning the union of their timing. */
export function mergeWithNext(words, wordId) {
    const idx = words.findIndex((w) => w.id === wordId);
    if (idx === -1 || idx === words.length - 1)
        return words;
    const a = words[idx];
    const b = words[idx + 1];
    const merged = {
        ...a,
        text: `${a.text}${b.text}`,
        startMs: Math.min(a.startMs, b.startMs),
        endMs: Math.max(a.endMs, b.endMs),
    };
    return [...words.slice(0, idx), merged, ...words.slice(idx + 2)];
}
/**
 * Split a caption segment after the Nth word. Returns two segments with
 * recomputed timing. Surrounding segments are the caller's responsibility.
 */
export function splitSegment(segment, afterWordIndex, words) {
    if (afterWordIndex < 0 || afterWordIndex >= segment.wordIds.length - 1) {
        return [segment];
    }
    const wordsById = indexWords(words);
    const leftIds = segment.wordIds.slice(0, afterWordIndex + 1);
    const rightIds = segment.wordIds.slice(afterWordIndex + 1);
    const left = recomputeSegmentTiming({ id: segment.id, wordIds: leftIds, startMs: 0, endMs: 0 }, wordsById);
    const right = recomputeSegmentTiming({ id: localId('seg'), wordIds: rightIds, startMs: 0, endMs: 0 }, wordsById);
    return [left, right];
}
/** Merge two adjacent segments into one, recomputing timing. */
export function mergeSegments(a, b, words) {
    const wordsById = indexWords(words);
    return recomputeSegmentTiming({ id: a.id, wordIds: [...a.wordIds, ...b.wordIds], startMs: 0, endMs: 0 }, wordsById);
}
/**
 * Manually adjust a word's timing (advanced control). Clamps so start < end and
 * optionally keeps it within neighbour bounds to avoid overlaps.
 */
export function retimeWord(words, wordId, startMs, endMs) {
    return words.map((w) => {
        if (w.id !== wordId)
            return w;
        const s = Math.min(startMs, endMs - 1);
        return { ...w, startMs: s, endMs: Math.max(endMs, s + 1) };
    });
}
//# sourceMappingURL=edits.js.map